import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getServiceBySlug, hrefToSlug } from "../../utils/serviceRoutes";
import { services } from "../../data/services";
import ServiceRequestModal from "../../components/ServiceRequestModal/ServiceRequestModal";

function deliverablesCategoryKey(category) {
  const c = (category || "").trim().toLowerCase();
  if (c === "design" || c.includes("design")) return "design";
  if (c === "cloud" || c.includes("cloud") || c.includes("devops"))
    return "cloud";
  return "engineering";
}

function humanizeTag(tag) {
  return tag.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
}

const ServicePage = () => {
  const { serviceName } = useParams();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const openRequestModal = useCallback(() => setRequestModalOpen(true), []);
  const closeRequestModal = useCallback(() => setRequestModalOpen(false), []);

  const service = useMemo(
    () => getServiceBySlug(serviceName),
    [serviceName]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!service) return;
    const title = t(`serviceCatalog.${service.id}.title`);
    document.title = `${title} — VizHacks`;
    return () => {
      document.title = "Vizhacks: Technology Consulting";
    };
  }, [service, t]);

  useEffect(() => {
    if (service) return;
    document.title = t("servicePage.notFoundDocumentTitle");
    return () => {
      document.title = "Vizhacks: Technology Consulting";
    };
  }, [service, t]);

  if (!service) {
    return (
      <div
        className="vh-service-page vh-service-page--notfound"
        dir={i18n.dir()}
      >
        <div className="container-n vh-service-page__shell vh-service-page__notfound-inner">
          <div className="vh-service-page__notfound-layout">
            <p className="vh-service-page__notfound-code" aria-hidden="true">
              {t("servicePage.notFoundCode")}
            </p>
            <div className="vh-service-page__notfound-copy">
              <p className="vh-service-page__eyebrow description-n">
                {t("servicePage.notFoundEyebrow")}
              </p>
              <h1 className="title-h2 vh-service-page__title">
                {t("servicePage.notFoundTitle")}
              </h1>
              <p className="description-n vh-service-page__lede">
                {t("servicePage.notFoundBody")}
              </p>
              <p className="description-n vh-service-page__notfound-hint">
                {t("servicePage.notFoundComingSoon")}
              </p>
              <Link className="btn-n btn-n--black vh-service-page__back" to="/">
                <span className="btn-n__text">{t("servicePage.backHome")}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const id = service.id;
  const title = t(`serviceCatalog.${id}.title`);
  const category = t(`serviceCatalog.${id}.category`);
  const tagline = t(`serviceCatalog.${id}.description`);

  const intro = t(`servicePageDetail.${id}.intro`, {
    defaultValue: t("servicePage.fallbackIntro", {
      description: tagline,
      title,
    }),
  });

  const highlights = t(`servicePageDetail.${id}.highlights`, {
    returnObjects: true,
  });
  const highlightList =
    Array.isArray(highlights) &&
    highlights.length > 0 &&
    typeof highlights[0] === "string"
      ? highlights
      : t("servicePage.defaultHighlights", { returnObjects: true });

  const customDeliverables = t(`servicePageDetail.${id}.deliverables`, {
    returnObjects: true,
  });
  const deliverablesCat = deliverablesCategoryKey(service.category);
  const categoryDeliverables = t(
    `servicePage.deliverablesByCategory.${deliverablesCat}`,
    { returnObjects: true }
  );
  const deliverableList =
    Array.isArray(customDeliverables) &&
    customDeliverables.length > 0 &&
    typeof customDeliverables[0] === "string"
      ? customDeliverables
      : Array.isArray(categoryDeliverables)
        ? categoryDeliverables
        : [];

  const customProcess = t(`servicePageDetail.${id}.process`, {
    returnObjects: true,
  });
  const defaultProcess = t("servicePage.defaultProcess", {
    returnObjects: true,
  });
  const processSteps =
    Array.isArray(customProcess) &&
    customProcess.length > 0 &&
    typeof customProcess[0] === "object"
      ? customProcess
      : Array.isArray(defaultProcess)
        ? defaultProcess
        : [];

  const heroMeta = t("servicePage.heroMeta", { returnObjects: true });
  const heroMetaList = Array.isArray(heroMeta) ? heroMeta : [];

  const related = services
    .filter((s) => s.category === service.category && s.id !== service.id)
    .slice(0, 4);

  return (
    <div className="vh-service-page" dir={i18n.dir()}>
      <div className="vh-service-page__hero">
        <div className="vh-service-page__hero-grid" aria-hidden="true" />
        <div className="container-n vh-service-page__hero-inner">
          <div className="vh-service-page__hero-row">
            <div className="vh-service-page__hero-copy">
              <nav
                className="vh-service-page__breadcrumb description-n"
                aria-label="Breadcrumb"
              >
                <Link to="/">{t("servicePage.breadcrumbHome")}</Link>
                <span className="vh-service-page__bc-sep" aria-hidden="true">
                  /
                </span>
                <span>{t("servicePage.breadcrumbCurrent")}</span>
                <span className="vh-service-page__bc-sep" aria-hidden="true">
                  /
                </span>
                <span className="vh-service-page__bc-current">{title}</span>
              </nav>
              <p className="vh-service-page__eyebrow">{category}</p>
              <h1 className="title-h1 vh-service-page__headline">{title}</h1>
              <p className="vh-service-page__tagline description-n">{tagline}</p>
              <div className="vh-service-page__hero-actions">
                <button
                  type="button"
                  className="btn-n btn-n--white vh-service-page__hero-request"
                  onClick={openRequestModal}
                >
                  <span className="btn-n__text">
                    {t("servicePage.ctaButton")}
                  </span>
                </button>
                <p className="description-n vh-service-page__hero-request-hint">
                  {t("servicePage.requestServiceHint")}
                </p>
              </div>
            </div>

            {heroMetaList.length > 0 ? (
              <section
                className="vh-service-page__hero-card"
                aria-labelledby="svc-hero-card-title"
              >
                <div className="vh-service-page__hero-card-glow" aria-hidden="true" />
                <h2 id="svc-hero-card-title" className="vh-service-page__hero-card-title">
                  {t("servicePage.heroSummary")}
                </h2>
                <ul className="vh-service-page__hero-meta">
                  {heroMetaList.map((line, i) => (
                    <li key={i} className="vh-service-page__hero-meta-item">
                      <span className="vh-service-page__hero-meta-icon" aria-hidden="true">
                        <span className="vh-service-page__hero-meta-dot" />
                      </span>
                      <span className="vh-service-page__hero-meta-text">{line}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        </div>
      </div>

      <div className="container-n vh-service-page__shell">
        <div className="vh-service-page__layout">
          <article className="vh-service-page__main">
            <section
              className="vh-service-page__section vh-service-page__section--tags"
              aria-labelledby="svc-fit"
            >
              <div className="vh-service-page__section-head">
                <span className="vh-service-page__section-accent" aria-hidden="true" />
                <h2 id="svc-fit" className="title-h4 vh-service-page__h">
                  {t("servicePage.sectionFit")}
                </h2>
              </div>
              <p className="vh-service-page__section-lede description-n">
                {t("servicePage.fitLede")}
              </p>
              <ul className="vh-service-page__tags">
                {(service.tags || []).map((tag) => {
                  const tk = `assessment.tags.${tag}`;
                  const label = t(tk);
                  const text = label === tk ? humanizeTag(tag) : label;
                  return (
                    <li key={tag}>
                      <span className="vh-service-page__tag">{text}</span>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section
              className="vh-service-page__section vh-service-page__section--overview"
              aria-labelledby="svc-intro"
            >
              <div className="vh-service-page__section-head">
                <span className="vh-service-page__section-accent" aria-hidden="true" />
                <h2 id="svc-intro" className="title-h4 vh-service-page__h">
                  {t("servicePage.sectionOverview")}
                </h2>
              </div>
              <div className="vh-service-page__prose vh-service-page__prose--flow description-n">
                {intro.split("\n\n").map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </section>

            {deliverableList.length > 0 ? (
              <section
                className="vh-service-page__section"
                aria-labelledby="svc-deliverables"
              >
                <div className="vh-service-page__section-head">
                  <span className="vh-service-page__section-accent" aria-hidden="true" />
                  <h2 id="svc-deliverables" className="title-h4 vh-service-page__h">
                    {t("servicePage.sectionDeliverables")}
                  </h2>
                </div>
                <p className="vh-service-page__section-lede description-n">
                  {t("servicePage.deliverablesLead")}
                </p>
                <ul className="vh-service-page__checklist vh-service-page__checklist--plain">
                  {deliverableList.map((item, i) => (
                    <li key={i} className="vh-service-page__deliverable-row">
                      <span className="vh-service-page__check-mark" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path
                            d="M16.25 5.625L8.125 13.75L3.75 9.375"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <p className="vh-service-page__deliverable-text description-n">
                        {item}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section
              className="vh-service-page__section vh-service-page__section--highlights"
              aria-labelledby="svc-highlights"
            >
              <div className="vh-service-page__section-head">
                <span className="vh-service-page__section-accent" aria-hidden="true" />
                <h2 id="svc-highlights" className="title-h4 vh-service-page__h">
                  {t("servicePage.sectionHighlights")}
                </h2>
              </div>
              <ul className="vh-service-page__highlights vh-service-page__highlights--plain">
                {highlightList.map((text, i) => (
                  <li key={i} className="vh-service-page__highlight-line">
                    <span className="vh-service-page__highlight-index" aria-hidden="true">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="vh-service-page__highlight-text description-n">{text}</p>
                  </li>
                ))}
              </ul>
            </section>

            {processSteps.length > 0 ? (
              <section
                className="vh-service-page__section vh-service-page__section--engage"
                aria-labelledby="svc-process"
              >
                <div className="vh-service-page__section-head">
                  <span className="vh-service-page__section-accent" aria-hidden="true" />
                  <h2 id="svc-process" className="title-h4 vh-service-page__h">
                    {t("servicePage.sectionProcess")}
                  </h2>
                </div>
                <p className="vh-service-page__section-lede vh-service-page__engage-lede description-n">
                  {t("servicePage.processLede")}
                </p>
                <ol className="vh-service-page__timeline">
                  {processSteps.map((step, i) => (
                    <li key={i} className="vh-service-page__timeline-item">
                      <div className="vh-service-page__timeline-gutter">
                        <div className="vh-service-page__timeline-node" aria-hidden="true">
                          <span>{i + 1}</span>
                        </div>
                      </div>
                      <div className="vh-service-page__timeline-body">
                        <h3 className="vh-service-page__timeline-title title-h4">
                          {step.title}
                        </h3>
                        <p className="description-n vh-service-page__timeline-text">
                          {step.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}

            {related.length > 0 ? (
              <section
                className="vh-service-page__section"
                aria-labelledby="svc-related"
              >
                <div className="vh-service-page__section-head">
                  <span className="vh-service-page__section-accent" aria-hidden="true" />
                  <h2 id="svc-related" className="title-h4 vh-service-page__h">
                    {t("servicePage.sectionRelated")}
                  </h2>
                </div>
                <ul className="vh-service-page__related-grid">
                  {related.map((s) => (
                    <li key={s.id}>
                      <Link
                        className="vh-service-page__related-card"
                        to={`/service/${hrefToSlug(s.href)}`}
                      >
                        <div className="vh-service-page__related-card-top">
                          <span className="vh-service-page__related-cat">
                            {t(`serviceCatalog.${s.id}.category`)}
                          </span>
                        </div>
                        <h3 className="vh-service-page__related-card-title">
                          {t(`serviceCatalog.${s.id}.title`)}
                        </h3>
                        <p className="vh-service-page__related-card-desc description-n">
                          {t(`serviceCatalog.${s.id}.description`)}
                        </p>
                        <div className="vh-service-page__related-card-footer">
                          <span className="vh-service-page__related-card-cta">
                            {t("serviceCard.viewService")}
                            <span
                              className="vh-service-page__related-arrow"
                              aria-hidden="true"
                            >
                              →
                            </span>
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </article>

          <aside
            className="vh-service-page__aside"
            aria-label={t("servicePage.asideLabel")}
          >
            <div className="vh-service-page__cta-card vh-service-page__cta-card--primary">
              <p className="vh-service-page__cta-eyebrow">
                {t("servicePage.asideLabel")}
              </p>
              <h2 className="title-h4 vh-service-page__cta-title">
                {t("servicePage.ctaTitle", { title })}
              </h2>
              <p className="description-n vh-service-page__cta-body">
                {t("servicePage.ctaBody")}
              </p>
              <button
                type="button"
                className="btn-n btn-n--black vh-service-page__cta-btn"
                onClick={openRequestModal}
              >
                <span className="btn-n__text">{t("servicePage.ctaButton")}</span>
              </button>
              <Link
                className="vh-service-page__text-link description-n"
                to="/"
              >
                {t("servicePage.backHome")}
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <ServiceRequestModal
        open={requestModalOpen}
        onClose={closeRequestModal}
        serviceTitle={title}
      />
    </div>
  );
};

export default ServicePage;
